import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Landmark, ArrowDownLeft, ArrowUpRight, RefreshCw, CheckCircle,
  XCircle, Clock, Wallet, Users, FileText, Search, Loader2,
  DollarSign, TrendingUp, AlertTriangle,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import { toast } from '../../hooks/use-toast';
import { EscrowPanel } from '../../components/admin/EscrowPanel';
import { WalletDetailDialog } from '../../components/admin/WalletDetailDialog';

const TABS = [
  { key: 'withdrawals', label: 'Solicitudes de Retiro', icon: ArrowUpRight },
  { key: 'wallets', label: 'Billeteras', icon: Wallet },
  { key: 'escrow', label: 'Custodia', icon: Clock },
  { key: 'history', label: 'Historial Global', icon: FileText },
];

const TYPE_LABELS = {
  recharge: 'Recarga',
  ad_spend: 'Gasto Ads',
  delivery_earning: 'Ingreso Repartidor',
  seller_earning: 'Ingreso Vendedor',
  withdrawal: 'Retiro',
  manual_adjustment: 'Ajuste Manual',
  order_wallet_payment: 'Pago Pedido',
  order_card_payment: 'Pago Tarjeta',
  subscription: 'Suscripcion',
};

const TYPE_COLORS = {
  recharge: 'text-green-400',
  seller_earning: 'text-emerald-400',
  delivery_earning: 'text-cyan-400',
  withdrawal: 'text-orange-400',
  ad_spend: 'text-red-400',
  manual_adjustment: 'text-purple-400',
  order_wallet_payment: 'text-red-400',
  order_card_payment: 'text-blue-400',
};

const STATUS_BADGE = {
  pending: { bg: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Clock },
  completed: { bg: 'bg-green-500/15 text-green-400 border-green-500/30', icon: CheckCircle },
  rejected: { bg: 'bg-red-500/15 text-red-400 border-red-500/30', icon: XCircle },
  failed: { bg: 'bg-red-500/15 text-red-400 border-red-500/30', icon: XCircle },
};

const AdminBank = () => {
  const [tab, setTab] = useState('withdrawals');
  const [withdrawals, setWithdrawals] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [processing, setProcessing] = useState(null);
  const [detailUserId, setDetailUserId] = useState(null);

  const load = useCallback(async () => {
    if (tab === 'escrow') return;
    setLoading(true);
    try {
      if (tab === 'withdrawals') {
        const data = await adminAPI.getBankWithdrawals();
        setWithdrawals(data);
      } else if (tab === 'wallets') {
        const data = await adminAPI.getBankWallets();
        setWallets(data);
      } else {
        const data = await adminAPI.getBankTransactions({ limit: 100 });
        setTransactions(data);
      }
    } catch {
      toast({ title: 'Error cargando datos', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (txnId) => {
    if (!window.confirm('Confirmas que ya realizaste la transferencia SPEI?')) return;
    setProcessing(txnId);
    try {
      await adminAPI.approveWithdrawal(txnId);
      toast({ title: 'Retiro aprobado' });
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessing(null); }
  };

  const handleReject = async (txnId) => {
    if (!window.confirm('Rechazar retiro? El saldo sera devuelto al usuario.')) return;
    setProcessing(txnId);
    try {
      await adminAPI.rejectWithdrawal(txnId);
      toast({ title: 'Retiro rechazado y saldo devuelto' });
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessing(null); }
  };

  const handleAdjust = async () => {
    const amt = parseFloat(adjustAmount);
    if (!amt || amt === 0) return;
    setProcessing('adjust');
    try {
      await adminAPI.adjustWallet(adjustModal.user_id, amt, adjustNote);
      toast({ title: 'Saldo ajustado' });
      setAdjustModal(null);
      setAdjustAmount('');
      setAdjustNote('');
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessing(null); }
  };

  // Stats for withdrawals tab
  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
  const pendingTotal = pendingWithdrawals.reduce((s, w) => s + Math.abs(w.amount), 0);
  const totalWalletBalance = wallets.reduce((s, w) => s + (w.wallet_balance || 0), 0);
  const totalPending = wallets.reduce((s, w) => s + (w.pending_balance || 0), 0);

  const filtered = (arr) => {
    if (!searchTerm) return arr;
    const q = searchTerm.toLowerCase();
    return arr.filter(x =>
      (x.user_name || '').toLowerCase().includes(q) ||
      (x.user_email || '').toLowerCase().includes(q) ||
      (x.name || '').toLowerCase().includes(q) ||
      (x.email || '').toLowerCase().includes(q)
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-bank">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-emerald-500/20 to-green-600/20 rounded-xl border border-emerald-700/30">
              <Landmark className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="admin-bank-title">Banco Central</h1>
              <p className="text-gray-400 text-sm mt-0.5">Centro de Control Financiero</p>
            </div>
          </div>
          <Button variant="ghost" onClick={load} className="text-gray-400 hover:text-white" data-testid="refresh-bank">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-amber-500/10 border-amber-700/30 p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-xs text-amber-400/70 uppercase font-medium">Retiros Pendientes</p>
                <p className="text-2xl font-bold text-amber-300">{pendingWithdrawals.length}</p>
                <p className="text-xs text-amber-400/50">{formatPrice(pendingTotal)} por procesar</p>
              </div>
            </div>
          </Card>
          <Card className="bg-green-500/10 border-green-700/30 p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-xs text-green-400/70 uppercase font-medium">Saldo Total Usuarios</p>
                <p className="text-2xl font-bold text-green-300">{formatPrice(totalWalletBalance)}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-purple-500/10 border-purple-700/30 p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-xs text-purple-400/70 uppercase font-medium">En Transito (Escrow)</p>
                <p className="text-2xl font-bold text-purple-300">{formatPrice(totalPending)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-800 rounded-xl p-1" data-testid="bank-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === t.key ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'
                }`} data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="bg-gray-800 border-gray-700 text-white pl-10" data-testid="bank-search" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* ═══ WITHDRAWALS TAB ═══ */}
            {tab === 'withdrawals' && (
              <Card className="bg-gray-800 border-gray-700 overflow-hidden" data-testid="withdrawals-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/50">
                        <th className="text-left p-3 text-gray-400 font-medium">Usuario</th>
                        <th className="text-left p-3 text-gray-400 font-medium">CLABE</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Banco</th>
                        <th className="text-right p-3 text-gray-400 font-medium">Monto</th>
                        <th className="text-center p-3 text-gray-400 font-medium">Estado</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Fecha</th>
                        <th className="text-center p-3 text-gray-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered(withdrawals).length === 0 ? (
                        <tr><td colSpan={7} className="p-8 text-center text-gray-500">Sin retiros registrados</td></tr>
                      ) : filtered(withdrawals).map((w, i) => {
                        const sb = STATUS_BADGE[w.status] || STATUS_BADGE.pending;
                        const SIcon = sb.icon;
                        return (
                          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`withdrawal-row-${i}`}>
                            <td className="p-3">
                              <p className="text-white font-medium">{w.user_name || '—'}</p>
                              <p className="text-gray-500 text-xs">{w.user_email || '—'}</p>
                            </td>
                            <td className="p-3 font-mono text-xs text-gray-300">{w.clabe_account || '—'}</td>
                            <td className="p-3 text-gray-300 text-xs">{w.bank_name || '—'}<br /><span className="text-gray-500">{w.account_holder || ''}</span></td>
                            <td className="p-3 text-right font-mono font-bold text-orange-400">{formatPrice(Math.abs(w.amount))}</td>
                            <td className="p-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${sb.bg}`}>
                                <SIcon className="w-3 h-3" /> {w.status}
                              </span>
                            </td>
                            <td className="p-3 text-gray-500 text-xs">{w.created_at ? new Date(w.created_at).toLocaleString('es-MX') : '—'}</td>
                            <td className="p-3 text-center">
                              {w.status === 'pending' ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Button size="sm" onClick={() => handleApprove(w.txn_id)}
                                    disabled={processing === w.txn_id}
                                    className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-2" data-testid={`approve-${i}`}>
                                    {processing === w.txn_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" />SPEI</>}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleReject(w.txn_id)}
                                    disabled={processing === w.txn_id}
                                    className="text-red-400 hover:text-red-300 h-7 text-xs px-2" data-testid={`reject-${i}`}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-gray-600 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* ═══ WALLETS TAB ═══ */}
            {tab === 'wallets' && (
              <Card className="bg-gray-800 border-gray-700 overflow-hidden" data-testid="wallets-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/50">
                        <th className="text-left p-3 text-gray-400 font-medium">Usuario</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Rol</th>
                        <th className="text-right p-3 text-gray-400 font-medium">Disponible</th>
                        <th className="text-right p-3 text-gray-400 font-medium">En Transito</th>
                        <th className="text-left p-3 text-gray-400 font-medium">CLABE</th>
                        <th className="text-center p-3 text-gray-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered(wallets).length === 0 ? (
                        <tr><td colSpan={6} className="p-8 text-center text-gray-500">Sin usuarios</td></tr>
                      ) : filtered(wallets).map((u, i) => (
                        <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`wallet-row-${i}`}>
                          <td className="p-3">
                            <p className="text-white font-medium">{u.name || '—'}</p>
                            <p className="text-gray-500 text-xs">{u.email || '—'}</p>
                          </td>
                          <td className="p-3">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 capitalize">{u.role || 'buyer'}</span>
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-green-400">{formatPrice(u.wallet_balance || 0)}</td>
                          <td className="p-3 text-right font-mono text-purple-400">{formatPrice(u.pending_balance || 0)}</td>
                          <td className="p-3 font-mono text-xs text-gray-400">{u.clabe_account || '—'}</td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => setDetailUserId(u.user_id)}
                              className="text-sky-400 hover:text-sky-300 h-7 text-xs" data-testid={`wallet-detail-${i}`}>
                              <FileText className="w-3 h-3 mr-1" /> Ver
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setAdjustModal(u)}
                              className="text-purple-400 hover:text-purple-300 h-7 text-xs" data-testid={`adjust-${i}`}>
                              <DollarSign className="w-3 h-3 mr-1" /> Ajustar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {tab === 'escrow' && <EscrowPanel />}

            {/* ═══ HISTORY TAB ═══ */}
            {tab === 'history' && (
              <Card className="bg-gray-800 border-gray-700 overflow-hidden" data-testid="history-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/50">
                        <th className="text-left p-3 text-gray-400 font-medium">Tipo</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Usuario</th>
                        <th className="text-right p-3 text-gray-400 font-medium">Monto</th>
                        <th className="text-center p-3 text-gray-400 font-medium">Estado</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Nota</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered(transactions).length === 0 ? (
                        <tr><td colSpan={6} className="p-8 text-center text-gray-500">Sin transacciones</td></tr>
                      ) : filtered(transactions).map((t, i) => {
                        const sb = STATUS_BADGE[t.status] || STATUS_BADGE.pending;
                        const SIcon = sb.icon;
                        const typeColor = TYPE_COLORS[t.type] || 'text-gray-400';
                        return (
                          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`txn-row-${i}`}>
                            <td className="p-3">
                              <span className={`text-xs font-medium ${typeColor}`}>{TYPE_LABELS[t.type] || t.type}</span>
                            </td>
                            <td className="p-3">
                              <p className="text-white text-xs">{t.user_name || '—'}</p>
                              <p className="text-gray-500 text-[10px]">{t.user_email || t.user_id?.slice(0, 8) || '—'}</p>
                            </td>
                            <td className={`p-3 text-right font-mono font-bold ${t.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {t.amount >= 0 ? '+' : ''}{formatPrice(Math.abs(t.amount))}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sb.bg}`}>
                                <SIcon className="w-2.5 h-2.5" /> {t.status}
                              </span>
                            </td>
                            <td className="p-3 text-gray-500 text-xs max-w-[150px] truncate">{t.note || t.order_id?.slice(0, 8) || '—'}</td>
                            <td className="p-3 text-gray-500 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString('es-MX') : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* ═══ ADJUST MODAL ═══ */}
        <Dialog open={!!adjustModal} onOpenChange={() => { setAdjustModal(null); setAdjustAmount(''); setAdjustNote(''); }}>
          <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="adjust-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <DollarSign className="w-5 h-5 text-purple-400" /> Ajustar Saldo
              </DialogTitle>
            </DialogHeader>
            {adjustModal && (
              <div className="space-y-4">
                <div className="bg-gray-900 rounded-lg p-3">
                  <p className="text-white font-medium">{adjustModal.name}</p>
                  <p className="text-gray-400 text-sm">{adjustModal.email}</p>
                  <p className="text-green-400 font-mono mt-1">Saldo actual: {formatPrice(adjustModal.wallet_balance || 0)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Monto (positivo = sumar, negativo = restar)</label>
                  <Input type="number" step="0.01" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                    placeholder="Ej: 500 o -100" className="bg-gray-900 border-gray-600 text-white font-mono" data-testid="adjust-amount" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Nota / Razon</label>
                  <Input value={adjustNote} onChange={e => setAdjustNote(e.target.value)}
                    placeholder="Compensacion, correccion, etc." className="bg-gray-900 border-gray-600 text-white" data-testid="adjust-note" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleAdjust} disabled={processing === 'adjust' || !adjustAmount || parseFloat(adjustAmount) === 0}
                    className="bg-purple-600 hover:bg-purple-700 text-white flex-1" data-testid="adjust-confirm">
                    {processing === 'adjust' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar Ajuste'}
                  </Button>
                  <Button variant="outline" onClick={() => setAdjustModal(null)} className="border-gray-600 text-gray-300">
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <WalletDetailDialog userId={detailUserId} onClose={() => setDetailUserId(null)} />
      </div>
    </AdminLayout>
  );
};

export default AdminBank;
